import React, { FC, useEffect, useMemo, useState } from "react";
import { Modal, Select as PdSelect } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import { usePermissions } from "~/shared/context/PermissionContext";
import { processArray } from "~/shared/lib/processArray";
import {
  UPLOAD_DATA_OPTIONS,
  UploadDataOptionConfig,
} from "~/widgets/UploadDataModal/model/options";
import {
  UploadDataFormState,
  UploadDataModalProps,
  UploadDataType,
} from "~/widgets/UploadDataModal/model/types";

import s from "./UploadDataModal.less";

const FORM_ID = "upload-data-form";

// до первого ответа формы кнопка сабмита отключена (файл ещё не выбран)
const INITIAL_FORM_STATE: UploadDataFormState = { submitDisabled: true };

const UploadDataModal: FC<UploadDataModalProps> = ({
  open,
  onClose,
  refetch,
  types,
}) => {
  const { t } = useTranslation();
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const [activeType, setActiveType] = useState<UploadDataType | null>(null);
  const [formState, setFormState] =
    useState<UploadDataFormState>(INITIAL_FORM_STATE);

  // types задан и непустой → выбор типа залочен переданным списком
  const isTypeLocked = !!types && types.length > 0;

  const filteredOptions = useMemo(() => {
    if (loadingPermissions) {
      return [];
    }
    return (
      processArray(
        hasPermission,
        UPLOAD_DATA_OPTIONS,
      ) as UploadDataOptionConfig[]
    ).filter((option) => !isTypeLocked || types.includes(option.value));
  }, [loadingPermissions, hasPermission, isTypeLocked, types]);

  useEffect(() => {
    if (!open) {
      setActiveType(null);
      setFormState(INITIAL_FORM_STATE);
      return;
    }
    if (filteredOptions.length > 0 && !activeType) {
      setActiveType(filteredOptions[0].value);
    }
  }, [open, filteredOptions, activeType]);

  useEffect(() => {
    setFormState(INITIAL_FORM_STATE);
  }, [activeType]);

  const typeOptions = useMemo(
    () =>
      filteredOptions.map((option) => ({
        label: t(`uploads.${option.value}`),
        value: option.value,
      })),
    [filteredOptions, t],
  );

  const activeOption = filteredOptions.find(
    (option) => option.value === activeType,
  );
  const ActiveForm = activeOption?.component;

  return (
    <Modal
      open={open}
      title={formState.title ?? t("uploads.upload_data")}
      onCancel={onClose}
      centered
      closable
      size="small"
      rootClassName={formState.customFooter ? s.hiddenFooterRoot : undefined}
      footer={formState.customFooter ? null : undefined}
      cancelButton={
        formState.customFooter ? undefined : { children: t("ui.cancel") }
      }
      okButton={
        formState.customFooter
          ? undefined
          : {
              children: t("ui.upload_file"),
              htmlType: "submit",
              form: FORM_ID,
              loading: formState.loading,
              disabled: formState.submitDisabled || !ActiveForm,
            }
      }
      {...(formState.width ? { width: formState.width } : {})}
    >
      <div className={s.content}>
        <div className={s.field}>
          <span className={s.fieldLabel}>{t("uploads.upload_type")}</span>
          <PdSelect
            value={activeType}
            onChange={(value: UploadDataType) => setActiveType(value)}
            options={typeOptions}
            disabled={isTypeLocked}
            style={{ width: "100%" }}
          />
        </div>
        {open && ActiveForm && (
          <ActiveForm
            formId={FORM_ID}
            onClose={onClose}
            refetch={refetch}
            onStateChange={setFormState}
          />
        )}
      </div>
    </Modal>
  );
};

export default UploadDataModal;
